import * as React from "react";
import { connect, ConnectedProps } from "react-redux";

import * as actions from "../../actionCreator";
import { GlobalApplicationState } from "globalApplicationState";
import moment from "moment";
import {
    PostFilterValues,
    PostListingPage,
    TAB_IDS
} from "../../models";
import PostList from "./postList";
import ErrorSnackbar from "modules/common/components/snackbars/errorSnackbar";
import LoadingOverlay from "modules/common/components/loadingOverlay";
import Tabs from "pages/common/tabs";
import { tagsApi } from "api/instances";
import { TenantSettingsTagGroup } from "modules/settings";
import { UserRoleStrings } from "modules/authorization/models";
import { push } from "react-router-redux";

import { QUERY_PARAM_KEYS } from "modules/common/hooks/useQueryParams";
import { setShouldDisplayNav } from "modules/adminLayout/actionCreator";
import CreatedByFilter, { CreatedBySelectValues } from "modules/common/components/authoring/createdByFilter";
import SuccessSnackbar from "modules/common/components/snackbars/successSnackbar";
import { ContentType } from "modules/common/models";

import PublishedPostDialog from "../post-creation/dialogs/publishedPostDialog";
import { getHighestRole } from "utils/userRoleUtils";
import "../../../common/components/authoring/styles/authoringListing.sass";

class PostListing extends React.Component<PropsWithRedux, ComponentState> {
    constructor(props: PropsWithRedux) {
        super(props);

        this.state = {
            showingPublishErrorsDialog: false,
            availableTopics: [],
            topicFilters: []
        };
    }

    public async componentDidMount() {
        moment.locale("en");

        if (!this.props.shouldDisplayNav)
            this.props.setShouldDisplayNav(true);

        if (this.props.topicFilters) this.setState({topicFilters: this.props.topicFilters})

        const queryParams = new URLSearchParams(window.location.search);

        const allPageNumber = +(queryParams.get(QUERY_PARAM_KEYS.ALL_PAGE_NUMBER) ?? 1);
        const publishedPageNumber = +(queryParams.get(QUERY_PARAM_KEYS.PUBLISHED_PAGE_NUMBER) ?? 1);
        const scheduledPageNumber = +(queryParams.get(QUERY_PARAM_KEYS.SCHEDULED_PAGE_NUMBER) ?? 1);
        const draftPageNumber = +(queryParams.get(QUERY_PARAM_KEYS.DRAFT_PAGE_NUMBER) ?? 1);
        const submissionPageNumber = +(queryParams.get(QUERY_PARAM_KEYS.SUBMISSION_PAGE_NUMBER) ?? 1);

        let currentTab = +(queryParams.get(QUERY_PARAM_KEYS.TAB_NUMBER) ?? TAB_IDS.DRAFTS);
        if (currentTab >= TAB_IDS.__LENGTH || currentTab < 0) {
            currentTab = TAB_IDS.DRAFTS;
        }

        const freshLoad = (!queryParams.has(QUERY_PARAM_KEYS.ALL_PAGE_NUMBER) || !queryParams.has(QUERY_PARAM_KEYS.PUBLISHED_PAGE_NUMBER)
            || !queryParams.has(QUERY_PARAM_KEYS.SCHEDULED_PAGE_NUMBER) || !queryParams.has(QUERY_PARAM_KEYS.DRAFT_PAGE_NUMBER) || !queryParams.has(QUERY_PARAM_KEYS.TAB_NUMBER));

        if (freshLoad) {
            let newUrl = this.generateUrl(currentTab, allPageNumber, publishedPageNumber, scheduledPageNumber, draftPageNumber, submissionPageNumber);

            window.history.replaceState(null, "", newUrl);
        }

        this.onSelectTab(currentTab, true);
        await this.getTopics();
        this.fetchLists(allPageNumber, publishedPageNumber, scheduledPageNumber, draftPageNumber, submissionPageNumber);
    }

    private shouldShowLoadingOverlay = () => {
        return (this.props.publishing
            || this.props.deleting
            || this.props.fetching
            || this.props.unpublishing)
            && !this.props.shouldShowPublishedDialog
            && !this.state.showingPublishErrorsDialog;
    }

    public shouldComponentUpdate(nextProps: PropsWithRedux, nextState: ComponentState) {
        return !(this.props.publishing && !nextProps.publishing && !nextState.showingPublishErrorsDialog); //to ensure publishing state is shown
    }

    public componentDidUpdate(prevProps: PropsWithRedux, prevState: ComponentState) {
        if (
            Object.keys(prevProps.publishValidationErrors.errors).length === 0
            && Object.keys(this.props.publishValidationErrors.errors).length > 0
        ) {
            this.setState(prevState => ({ ...prevState, showingPublishErrorsDialog: true }));
        }

        if (this.props.shouldFetch && (this.props.shouldFetch !== prevProps.shouldFetch)) {
            this.fetchLists();
        }
    }

    public render() {
        const { selectedTab } = this.props;

        return (
            <React.Fragment>
                <div className="authoring-tabs-container">
                    <Tabs
                        tabs={[
                            { label: this.getTabLabel("Drafts", this.props.draftPostList), dotColor: "grey", id: "draft-posts-tab-button" },
                            { 
                                label: this.getTabLabel(getHighestRole(this.props.user) === UserRoleStrings.CONTRIBUTOR ? "Pending Approval" : "Needs Review", this.props.submissionPostList), 
                                dotColor: "red",
                                id: "submission-posts-tab-button"
                            },
                            { label: this.getTabLabel("Scheduled", this.props.scheduledPostList), dotColor: "yellow", id: "scheduled-posts-tab-button" },
                            { label: this.getTabLabel("Published", this.props.publishedPostList), dotColor: "green", id: "published-posts-tab-button" },
                            { label: this.getTabLabel("All", this.props.allPostList), id: "all-posts-tab-button" },
                        ]}
                        selectedTab={selectedTab}
                        onSelectTab={this.onSelectTab}
                    />

                    <CreatedByFilter
                        selectedFilter={this.getTabFilters().createdByMeOnly ? CreatedBySelectValues.VIEW_MY : CreatedBySelectValues.VIEW_ALL}
                        content={ContentType.Post}
                        onSelect={this.onSelectPostCreatedBy}
                    />
                </div>

                <PostList
                    filters={this.getTabFilters(TAB_IDS.ALL)}
                    show={selectedTab === TAB_IDS.ALL}
                    page={this.props.allPostList}
                    fetchPage={this.fetchAllPostList}
                    availableTopics={this.state.availableTopics}
                />
                <PostList
                    filters={this.getTabFilters(TAB_IDS.PUBLISHED)}
                    show={selectedTab === TAB_IDS.PUBLISHED}
                    page={this.props.publishedPostList}
                    fetchPage={this.fetchPublished}
                    availableTopics={this.state.availableTopics}
                />
                <PostList
                    filters={this.getTabFilters(TAB_IDS.SCHEDULED)}
                    show={selectedTab === TAB_IDS.SCHEDULED}
                    page={this.props.scheduledPostList}
                    fetchPage={this.fetchScheduled}
                    availableTopics={this.state.availableTopics}
                />
                <PostList
                    filters={this.getTabFilters(TAB_IDS.DRAFTS)}
                    show={selectedTab === TAB_IDS.DRAFTS}
                    page={this.props.draftPostList}
                    fetchPage={this.fetchDrafts}
                    availableTopics={this.state.availableTopics}
                />
                <PostList
                    filters={this.getTabFilters(TAB_IDS.SUBMISSIONS)}
                    show={selectedTab === TAB_IDS.SUBMISSIONS}
                    page={this.props.submissionPostList}
                    fetchPage={this.fetchSubmissions}
                    availableTopics={this.state.availableTopics}
                />
                {this.props.shouldShowPublishedDialog && <PublishedPostDialog/>}
                <SuccessSnackbar successMessage={ this.props.successMessage || "" } clearSuccessMessage={this.props.clearSuccessMessage}/>
                <ErrorSnackbar errorMessage={this.props.errorMessage} clearErrorMessage={this.props.clearErrorMessage} />
                <LoadingOverlay absolute={true} show={this.shouldShowLoadingOverlay()} />
            </React.Fragment>
        );
    }

    private getTabFilters = (tabId?: TAB_IDS): PostFilterValues => {
        switch (tabId ?? this.props.selectedTab) {
            case TAB_IDS.DRAFTS:
                return this.props.draftPostList.filters;
            case TAB_IDS.SUBMISSIONS:
                return this.props.submissionPostList.filters;
            case TAB_IDS.SCHEDULED:
                return this.props.scheduledPostList.filters;
            case TAB_IDS.PUBLISHED:
                return this.props.publishedPostList.filters;
            case TAB_IDS.ALL:
            default:
                return this.props.allPostList.filters;
        }
    }

    // apply this filter to all tabs
    private onSelectPostCreatedBy = async (selection: CreatedBySelectValues) => {
        const createdByMeOnly = selection === CreatedBySelectValues.VIEW_MY;
        this.fetchLists(1, 1, 1, 1, 1, createdByMeOnly)
    }

    private fetchLists = (
        allPageNumber: number = 1,
        publishedPageNumber: number = 1,
        draftsPageNumber: number = 1,
        scheduledPageNumber: number = 1,
        submissionPageNumber: number = 1,
        createdByMeOnly?: boolean,
    ) => {
        const createdByMeForAll = createdByMeOnly ?? this.props.allPostList.filters.createdByMeOnly;
        this.fetchAllPostList(
            allPageNumber,
            { ...this.props.allPostList.filters, createdByMeOnly: createdByMeForAll },
            undefined,
            true
        );
        
        const tags = this.state.topicFilters.length > 0 ? this.state.topicFilters : this.props.allPostList.filters.tags;
        this.fetchPublished(
            publishedPageNumber,
            { ...this.props.publishedPostList.filters, createdByMeOnly: createdByMeForAll, tags },
            undefined,
            true
        );

        this.fetchDrafts(
            draftsPageNumber,
            { ...this.props.draftPostList.filters, createdByMeOnly: createdByMeForAll },
            undefined,
            true
        );

        this.fetchScheduled(
            scheduledPageNumber,
            { ...this.props.scheduledPostList.filters, createdByMeOnly: createdByMeForAll },
            undefined,
            true
        );

        this.fetchSubmissions(
            submissionPageNumber,
            { ...this.props.submissionPostList.filters, createdByMeOnly: createdByMeForAll },
            undefined,
            true
        );

        this.props.clearShouldFetch();
    }

    private fetchSubmissions = (pageNumber: number, filters: PostFilterValues, pageAmount?: number, skipUrlUpdate: boolean = false) => {
        if (!skipUrlUpdate) {
            let newUrl = this.generateUrl(this.props.selectedTab, undefined, undefined, undefined, undefined, pageNumber);
            window.history.replaceState(null, "", newUrl);
        }

        !this.props.submissionPostList.isFetching &&
            this.props.fetchSubmissionPosts(pageNumber, filters, pageAmount);
    }

    private getTabLabel = (label: string, page: PostListingPage): string => {
        if (!!page.currentPage)
            return `${label} (${page.totalPosts})`;
        return label;
    }

    private onSelectTab = (tabIndex: number, skipUrlUpdate: boolean = false) => {
        if (!skipUrlUpdate) {
            let newUrl = this.generateUrl(tabIndex, undefined, undefined, undefined, undefined);
            window.history.replaceState(null, "", newUrl);
        }

        this.props.onSelectTab(tabIndex);
    }

    //Leaving a page number as undefined means it will not be updated in the function call.
    private generateUrl = (
        tabNumber: number,
        allPageNumber?: number,
        publishedPageNumber?: number,
        scheduledPageNumber?: number,
        draftPageNumber?: number,
        submissionPageNumber?: number
    ) => {
        const queryParams = new URLSearchParams(window.location.search);
        let newUrl = `/${this.props.tenantId}/admin/posts`;

        const urlAllPageNumber = +(queryParams.get(QUERY_PARAM_KEYS.ALL_PAGE_NUMBER) ?? 1);
        const urlPublishedPageNumber = +(queryParams.get(QUERY_PARAM_KEYS.PUBLISHED_PAGE_NUMBER) ?? 1);
        const urlScheduledPageNumber = +(queryParams.get(QUERY_PARAM_KEYS.SCHEDULED_PAGE_NUMBER) ?? 1);
        const urlDraftPageNumber = +(queryParams.get(QUERY_PARAM_KEYS.DRAFT_PAGE_NUMBER) ?? 1);
        const urlDraftSubmissionNumber = +(queryParams.get(QUERY_PARAM_KEYS.SUBMISSION_PAGE_NUMBER) ?? 1);

        newUrl = newUrl + `?${QUERY_PARAM_KEYS.ALL_PAGE_NUMBER}=${allPageNumber ?? urlAllPageNumber}`;
        newUrl = newUrl + `&${QUERY_PARAM_KEYS.PUBLISHED_PAGE_NUMBER}=${publishedPageNumber ?? urlPublishedPageNumber}`;
        newUrl = newUrl + `&${QUERY_PARAM_KEYS.SCHEDULED_PAGE_NUMBER}=${scheduledPageNumber ?? urlScheduledPageNumber}`;
        newUrl = newUrl + `&${QUERY_PARAM_KEYS.DRAFT_PAGE_NUMBER}=${draftPageNumber ?? urlDraftPageNumber}`;
        newUrl = newUrl + `&${QUERY_PARAM_KEYS.SUBMISSION_PAGE_NUMBER}=${submissionPageNumber ?? urlDraftSubmissionNumber}`;
        newUrl = newUrl + `&${QUERY_PARAM_KEYS.TAB_NUMBER}=${tabNumber}`;

        return newUrl;
    }

    private fetchDrafts = (pageNumber: number, filters: PostFilterValues, pageAmount?: number, skipUrlUpdate: boolean = false) => {
        if (!skipUrlUpdate) {
            let newUrl = this.generateUrl(this.props.selectedTab, undefined, undefined, undefined, pageNumber);
            window.history.replaceState(null, "", newUrl);
        }

        !this.props.draftPostList.isFetching &&
            this.props.fetchDraftPosts(pageNumber, filters, pageAmount);
    }

    private fetchPublished = (pageNumber: number, filters: PostFilterValues, pageAmount?: number, skipUrlUpdate: boolean = false) => {
        if (!skipUrlUpdate) {
            let newUrl = this.generateUrl(this.props.selectedTab, undefined, pageNumber, undefined, undefined);
            window.history.replaceState(null, "", newUrl);
        }

        !this.props.publishedPostList.isFetching &&
            this.props.fetchPublishedPosts(pageNumber, filters, pageAmount);
    }

    private fetchScheduled = (pageNumber: number, filters: PostFilterValues, pageAmount?: number, skipUrlUpdate: boolean = false) => {
        if (!skipUrlUpdate) {
            let newUrl = this.generateUrl(this.props.selectedTab, undefined, undefined, pageNumber, undefined);
            window.history.replaceState(null, "", newUrl);
        }

        !this.props.scheduledPostList.isFetching &&
            this.props.fetchScheduledPosts(pageNumber, filters, pageAmount);
    }

    private fetchAllPostList = (pageNumber: number, filters: PostFilterValues, pageAmount?: number, skipUrlUpdate: boolean = false) => {
        if (!skipUrlUpdate) {
            let newUrl = this.generateUrl(this.props.selectedTab, pageNumber, undefined, undefined, undefined);
            window.history.replaceState(null, "", newUrl);
        }

        !this.props.allPostList.isFetching &&
            this.props.fetchAllPosts(pageNumber, filters, pageAmount);
    }

    private getTopics = async () => {
        let tagsToSet:TenantSettingsTagGroup[] = [];

        try {
            tagsToSet = await tagsApi.getUserTags(true);
        }
        catch (_) {}
        finally {
            this.setState({ availableTopics: tagsToSet });
        }
    }
}

interface ComponentProps {
    selectedTab:TAB_IDS;
    onSelectTab: (tabIndex: number) => void;
    topicFilters?: string[]
}

interface ComponentState {
    showingPublishErrorsDialog: boolean;
    availableTopics: TenantSettingsTagGroup[];
    topicFilters: string[];
}

const connector = connect(
    (state: GlobalApplicationState, ownProps: ComponentProps) => ({
        ...ownProps,
        allPostList: state.posts.allPostList,
        draftPostList: state.posts.draftPostList,
        expiredPostList: state.posts.expiredPostList,
        publishedPostList: state.posts.publishedPostList,
        scheduledPostList: state.posts.scheduledPostList,
        submissionPostList: state.posts.submissionPostList,
        deleting: state.posts.editor.deleting,
        errorMessage: state.posts.errorMessage,
        successMessage: state.posts.successMessage,
        fetching: state.posts.editor.fetching,
        submitting: state.posts.submitting,
        isInitialLoad: state.posts.isInitialLoad,
        publishing: state.posts.publishing,
        publishValidationErrors: state.posts.publishValidationErrors,
        shouldShowPublishedDialog: state.posts.publishConfirmation.shouldShowDialog,
        unpublishing: state.posts.unpublishing,
        shouldFetch: state.posts.shouldFetch,
        user: state.settings.currentUser,
        tenantId: state.tenant.id,
        shouldDisplayNav: state.adminLayout.shouldDisplayNav
    }),
    {
        clearErrorMessage: actions.clearPostAuthoringErrorMessage,
        clearSuccessMessage: actions.clearPostAuthoringSuccessMessage,
        clearShouldFetch: actions.clearShouldFetch,
        fetchAllPosts: actions.fetchAllPost,
        fetchDraftPosts: actions.fetchDraftPostList,
        fetchPublishedPosts: actions.fetchPublishedPosts,
        fetchScheduledPosts: actions.fetchScheduledPosts,
        fetchSubmissionPosts: actions.fetchSubmissionPosts,
        redirectTo: push,
        setShouldDisplayNav
    }
);
type PropsWithRedux = ConnectedProps<typeof connector>;

export default connector(PostListing);
